import React, { useState, useEffect } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { InputGroup } from 'components/input'
import { Badge } from 'components/badge'
import { CalendarIcon, UserIcon } from '@heroicons/react/24/outline'
import { BoatIcon, BoatBookingImage } from 'components/icons'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { GET_ALL_BOOKINGS_LIST } from 'graphql/booking/queries'
import { useLazyQuery } from '@apollo/client'
import { t } from 'i18next'
import i18n from 'i18next'
import Flatpickr from 'react-flatpickr'
import { useNavigate, useLocation } from 'react-router-dom'
import { customDateFormat, customTimeFormat } from 'utils/common'
import moment from 'moment'
import { getPageRange } from 'utils/paginationHelper'

// App Users module imports
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from '../constants'
import {
    getTripStatusBadge,
    isDayPassBooking,
    getBookingTravelDate,
    getPassengerCount,
    getFirstPassenger,
} from '../helpers'
import { LoadingState, ErrorState, EmptyState, BackButton } from '../components'
import { Divider } from 'components'

export default function UserBookings() {
    const navigate = useNavigate()
    const location = useLocation()
    
    // Get user info from navigation state
    const { userEmail, userName, userId } = location.state || {}

    // Redirect if no user email provided
    useEffect(() => {
        if (!userEmail) {
            navigate('/app-users')
        }
    }, [userEmail, navigate])

    const [isSearch, setIsSearch] = useState(false)
    const [filterObj, setFilterObj] = useState({
        booking_code: '',
        from_date: moment().subtract(6, 'months').format('YYYY-MM-DD'),
        to_date: moment().format('YYYY-MM-DD'),
    })

    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    const [bookingList, setBookingList] = useState(null)
    const [bookingCount, setBookingCount] = useState(0)

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(bookingCount / rowsPerPage)

    const [paginationGroup, setPaginationGroup] = useState(0)
    const pagesPerGroup = 10

    const handlePrevious = () => {
        if (page > 0) {
            const newPage = page - 1
            handlePageChange(newPage)
        }
    }

    const handleNext = () => {
        if (page < totalPages - 1) {
            const newPage = page + 1
            handlePageChange(newPage)
        }
    }

    useEffect(() => {
        if (userEmail) {
            setIsSearch(true)
            searchBookings(0)
        }
    }, [userEmail])

    const [getBookingList, { loading: bookingListLoading, error: bookingListError }] = useLazyQuery(
        GET_ALL_BOOKINGS_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getAllBookings.success && data.getAllBookings.code === '200') {
                    setBookingList(data.getAllBookings.bookings)
                    setBookingCount(data.getAllBookings.total_count)
                }
            },
        }
    )

    const handleClearFilter = () => {
        setFilterObj({
            booking_code: '',
            from_date: moment().subtract(6, 'months').format('YYYY-MM-DD'),
            to_date: moment().format('YYYY-MM-DD'),
        })
        setIsSearch(true)
        setBookingList(null)
        setPage(0)
        setPaginationGroup(0)
        searchBookings(0)
    }

    // Search bookings by user email
    const searchBookings = (currentPage = page) => {
        const offset = currentPage * rowsPerPage

        getBookingList({
            variables: {
                bookingFilter: {
                    booking_id: null,
                    search_string: userEmail, // Search by user email
                    status: null,
                    from_date: filterObj.from_date || null,
                    to_date: filterObj.to_date || null,
                },
                page: {
                    limit: rowsPerPage,
                    offset: offset,
                },
            },
        })
    }

    const handlePageChange = newPage => {
        setPage(newPage)
        setPaginationGroup(Math.floor(newPage / pagesPerGroup))
        searchBookings(newPage)
    }

    const onClickSearch = () => {
        setPage(0)
        setPaginationGroup(0)
        setRowsPerPage(10)
        setIsSearch(true)
        searchBookings(0)
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    const GoDetailsPage = (bookingId, travel_date, booking_type) => {
        navigate(`/manage-boat-booking/details/${bookingId}/${travel_date}/${booking_type || 'REG'}`, { 
            state: { 
                filterObj,
                fromAppUsers: true,
                userEmail,
                userName,
                userId
            } 
        })
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <BackButton onClick={() => navigate('/app-users')} label={t('Back to App Users')} />
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                            <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                    <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                </div>
                                <div>
                                    <span className="ms-3">{t('User Bookings')}</span>
                                    <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                        {t('Viewing bookings for')}: <span className="font-medium text-primary">{userName || userEmail}</span>
                                    </p>
                                </div>
                            </h1>
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-3xl gap-4">
                <div className="flex-auto w-[300px]">
                    <label htmlFor="region" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Search')}
                    </label>
                    <InputGroup>
                        <MagnifyingGlassIcon />
                        <input
                            id="booking_code"
                            name="booking_code"
                            type="text"
                            placeholder={t('Booking code')}
                            value={filterObj.booking_code}
                            onChange={e => {
                                setFilterObj({ ...filterObj, booking_code: e.target.value })
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </InputGroup>
                </div>
                <div className="flex-auto">
                    <label htmlFor="region" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Travel Date Range')}
                    </label>
                    <div>
                        <Flatpickr
                            options={{
                                mode: 'range',
                                dateFormat: 'Y-m-d',
                            }}
                            placeholder={t('From - To')}
                            value={[filterObj.from_date, filterObj.to_date]}
                            onChange={date => {
                                setFilterObj({
                                    ...filterObj,
                                    from_date: date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                    to_date: date[1] ? date[1].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                })
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                <div className="flex gap-y-2 justify-end mt-6">
                    <button
                        type="button"
                        onClick={onClickSearch}
                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                    >
                        {t('Search')}
                    </button>
                    <button
                        type="button"
                        onClick={handleClearFilter}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ms-2"
                    >
                        {t('Clear')}
                    </button>
                </div>
            </div> */}

            {/* Booking List */}
            {bookingListLoading && <LoadingState />}
            {bookingListError && <ErrorState message={t('No bookings found')} />}
            {!bookingListLoading && !bookingListError && isSearch && (!bookingList || bookingList?.length === 0) && (
                <EmptyState 
                    title={t('No bookings found!')} 
                    message={t('This user has no bookings in the selected date range')} 
                />
            )}
            {isSearch && !bookingListLoading && !bookingListError && bookingList?.length > 0 && (
                <div>
                    <div className="bg-white">
                        {bookingList.map(booking => (
                            <div key={booking.booking_id} className="mt-5">
                                <h2 className="sr-only">{t('Recent orders')}</h2>
                                <div className="w-full grid grid-cols-1 gap-4">
                                    <div className="max-w-full space-y-8 sm:px-4 lg:max-w-full lg:px-0">
                                        <div className="border-b border-t border-gray-200 shadow-sm sm:rounded-lg sm:border bg-slate-50">
                                            <div className="relative items-center border-b border-gray-200 px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 md:gap-y-3 p-2 pb-0">
                                                <div>
                                                    <dd className="mt-1 text-gray-500">{t('Booking Code')}</dd>
                                                    <dt className="font-medium text-gray-900 overflow-ellipsis">
                                                        {booking.booking_code || 'N/A'}
                                                    </dt>
                                                </div>
                                                <div>
                                                    <dd className="mt-1 text-gray-500">{t('Travel Date')}</dd>
                                                    <dt className="font-medium text-gray-900 flex items-center gap-2">
                                                        {customDateFormat(getBookingTravelDate(booking))}
                                                        {booking?.trip_segments?.[0]?.trip_status && (
                                                            <Badge 
                                                                color={getTripStatusBadge(booking?.trip_segments?.[0]?.trip_status).color} 
                                                                className="text-xs"
                                                            >
                                                                {getTripStatusBadge(booking?.trip_segments?.[0]?.trip_status).text}
                                                            </Badge>
                                                        )}
                                                    </dt>
                                                </div>
                                                {!isDayPassBooking(booking) && (
                                                    <>
                                                        <div>
                                                            <dd className="mt-1 text-gray-500">{t('From Station')}</dd>
                                                            <dt className="font-medium text-gray-900">
                                                                {booking?.trip_segments
                                                                    ? booking?.trip_segments[0]?.start?.location?.name?.en
                                                                    : 'N/A'}
                                                                {booking?.trip_segments && (
                                                                    <span className="text-gray-600 text-[13px] ps-2">
                                                                        {customTimeFormat(
                                                                            booking?.trip_segments[0]?.start?.departure_time
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </dt>
                                                        </div>
                                                        <div>
                                                            <dd className="mt-1 text-gray-500">{t('To Station')}</dd>
                                                            <dt className="font-medium text-gray-900">
                                                                {booking?.trip_segments
                                                                    ? booking?.trip_segments[0]?.end?.location?.name?.en
                                                                    : 'N/A'}
                                                                {booking?.trip_segments && (
                                                                    <span className="text-gray-600 text-[13px] ps-2">
                                                                        {customTimeFormat(
                                                                            booking?.trip_segments[0]?.end?.arrival_time
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </dt>
                                                        </div>
                                                    </>
                                                )}
                                                {isDayPassBooking(booking) && (
                                                    <>
                                                        <div>
                                                            <dd className="mt-1 text-gray-500">{t('Passengers')}</dd>
                                                            <dt className="font-medium text-gray-900">
                                                                {getPassengerCount(booking)} {t('Passengers')}
                                                            </dt>
                                                        </div>
                                                        <div>
                                                            <dd className="mt-1 text-gray-500">{t('Seat Type')}</dd>
                                                            <dt className="font-medium text-gray-900">
                                                                {booking?.passengers?.[0]?.seat_info?.seat_type_code || 'N/A'}
                                                            </dt>
                                                        </div>
                                                    </>
                                                )}
                                                <div className="text-end md:col-span-2 xl:col-span-1 flex items-end flex-col gap-y-1">
                                                    {(booking?.booking_type || booking?.metadata) && (
                                                        <p className="text-primary whitespace-nowrap bg-primary/20 absolute top-0 end-3 text-sm italic font font-semibold text-center rounded-b-md w-fit p-2 px-3 pb-1 pt-1">
                                                            {booking.booking_type?.description[i18n?.language || 'en']}
                                                            {booking.metadata?.booking_type?.description
                                                                ? ' with ' +
                                                                  booking?.metadata?.booking_type.description[
                                                                      i18n?.language || 'en'
                                                                  ]
                                                                : ''}
                                                        </p>
                                                    )}

                                                    <p className="font-extrabold mt-7 flex justify-normal items-center gap-x-2 text-[20px] text-primary mb-2">
                                                        <dd className="mt-1 text-gray-500 text-sm font-[400]">
                                                            {t('Ticket Cost')}
                                                        </dd>{' '}
                                                        {booking.total_price} BHD
                                                    </p>
                                                </div>
                                                <dl className="grid flex-1 grid-cols-2 gap-x-6 text-sm sm:col-span-3 sm:grid-cols-3 lg:col-span-2"></dl>
                                            </div>

                                            {/* Return Trip Information */}
                                            {booking?.trip_segments && booking.trip_segments.length > 1 && (
                                                <div className="relative items-center border-b border-gray-200 px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 md:gap-y-3 p-2 pb-0 bg-blue-50">
                                                    <div className="flex items-center gap-2">
                                                    </div>
                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('Travel Date')}</dd>
                                                        <dt className="font-medium text-gray-900 flex items-center gap-2">
                                                            {customDateFormat(booking?.trip_segments?.[1]?.travel_date)}
                                                            {booking?.trip_segments?.[1]?.trip_status && (
                                                                <Badge 
                                                                    color={getTripStatusBadge(booking?.trip_segments?.[1]?.trip_status).color} 
                                                                    className="text-xs"
                                                                >
                                                                    {getTripStatusBadge(booking?.trip_segments?.[1]?.trip_status).text}
                                                                </Badge>
                                                            )}
                                                        </dt>
                                                    </div>
                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('From Station')}</dd>
                                                        <dt className="font-medium text-gray-900">
                                                            {booking?.trip_segments[1]?.start?.location?.name?.en || 'N/A'}
                                                            {booking?.trip_segments[1] && (
                                                                <span className="text-gray-600 text-[13px] ps-2">
                                                                    {customTimeFormat(
                                                                        booking?.trip_segments[1]?.start?.departure_time
                                                                    )}
                                                                </span>
                                                            )}
                                                        </dt>
                                                    </div>
                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('To Station')}</dd>
                                                        <dt className="font-medium text-gray-900">
                                                            {booking?.trip_segments[1]?.end?.location?.name?.en || 'N/A'}
                                                            {booking?.trip_segments[1] && (
                                                                <span className="text-gray-600 text-[13px] ps-2">
                                                                    {customTimeFormat(
                                                                        booking?.trip_segments[1]?.end?.arrival_time
                                                                    )}
                                                                </span>
                                                            )}
                                                        </dt>
                                                    </div>
                                                    <div className="text-end md:col-span-2 xl:col-span-1 flex items-end flex-col gap-y-1">
                                                        <div className="flex items-center gap-2 mt-7">
                                                            <BoatBookingImage className="w-[30px] h-[30px] fill-blue-600" />
                                                            <div className="text-left">
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    {booking?.trip_segments[1]?.route?.boat?.name?.en || 'N/A'}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {booking?.trip_segments[1]?.trip?.trip_name?.en || 'N/A'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <h4 className="sr-only">Items</h4>
                                            <ul className="divide-y divide-gray-200">
                                                <li className="p-4">
                                                    <div className="flex sm:items-start">
                                                        <div className="flex-1 text-sm">
                                                            <div className="text-gray-900 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                                                <div className="shrink-0 ">
                                                                    <div className="flex items-center">
                                                                        <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74] mt-3 rounded-full bg-primary" />
                                                                        <div className="ps-3 pt-2">
                                                                            <div className="flex items-center gap-2">
                                                                                <h5 className="text-[18px]">
                                                                                    {isDayPassBooking(booking)
                                                                                        ? t('Day Pass')
                                                                                        : (booking?.trip_segments
                                                                                            ? booking?.trip_segments[0]?.route?.boat?.name?.en
                                                                                            : 'N/A')}
                                                                                </h5>
                                                                            </div>
                                                                            <p className="text-[14px] border-gray-300 sm:mt-2">
                                                                                {isDayPassBooking(booking)
                                                                                    ? (
                                                                                        <>
                                                                                            {getPassengerCount(booking)} {t('Passengers')}
                                                                                            {booking?.passengers?.filter(p => p.passenger?.special_needs).length > 0 && (
                                                                                                <span className="text-orange-600 font-medium">
                                                                                                    {' '}| {booking?.passengers?.filter(p => p.passenger?.special_needs).length} {t('Special Needs')}
                                                                                                </span>
                                                                                            )}
                                                                                        </>
                                                                                    )
                                                                                    : (
                                                                                        <>
                                                                                            {booking?.trip_segments
                                                                                                ? booking?.trip_segments[0]?.trip?.trip_name?.en
                                                                                                : 'N/A'}{' '}
                                                                                            |{' '}
                                                                                            {getPassengerCount(booking)}{' '}
                                                                                            {t('Passengers')}
                                                                                            {booking?.trip_segments && booking?.trip_segments[0]?.trip_meals_summary && booking?.trip_segments[0]?.trip_meals_summary.length > 0 && (
                                                                                                <span className="text-primary font-medium">
                                                                                                    {' '}| {booking?.trip_segments[0]?.trip_meals_summary.length} {t('Meals')}
                                                                                                </span>
                                                                                            )}
                                                                                        </>
                                                                                    )}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex shrink-0 md:place-content-end lg:place-content-center">
                                                                    <div className="flex items-center ">
                                                                        <CalendarIcon
                                                                            aria-hidden="true"
                                                                            className="h-5 w-5 text-green-500"
                                                                        />
                                                                        <p className="ml-2 text-sm font-100 text-gray-500">
                                                                            {t('Booked on')} <br /> {''}
                                                                            <span className="font-medium text-gray-700">
                                                                                {customDateFormat(
                                                                                    booking.user_audit?.created_date
                                                                                )}
                                                                            </span>
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex items-center ms-4 border-s-[1px] ps-3 border-gray-300 border-solid">
                                                                        <UserIcon
                                                                            aria-hidden="true"
                                                                            className="h-5 w-5 text-green-500"
                                                                        />
                                                                        <p className="ml-2 text-sm font-100 text-gray-500">
                                                                            {t('Booked by')} <br /> {''}
                                                                            <span className="font-medium text-gray-700">
                                                                                {getFirstPassenger(booking)?.family_name || 'N/A'}{' '}
                                                                                {getFirstPassenger(booking)?.given_name || ''}
                                                                            </span>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-col md:col-span-2 xl:col-span-1 md:place-content-end lg:items-end lg:place-items-end">
                                                                    <Badge
                                                                        color={
                                                                            booking.status?.description?.en ===
                                                                            'Confirmed'
                                                                                ? 'emerald'
                                                                                : booking.status?.description?.en ===
                                                                                  'Payment Processing'
                                                                                ? 'lime'
                                                                                : booking.status?.description?.en ===
                                                                                  'Cancelled'
                                                                                ? 'red'
                                                                                : 'zinc'
                                                                        }
                                                                        className="text-end w-fit mb-3"
                                                                    >
                                                                        {booking.status?.description?.en}
                                                                    </Badge>
                                                                    <div className="flex flex-col gap-2 lg:w-[250px]">
                                                                        <button
                                                                            className="bg-primary text-white hover:bg-primary/[0.9] py-2 px-8 rounded flex-none"
                                                                            onClick={() =>
                                                                                GoDetailsPage(
                                                                                    booking.booking_id,
                                                                                    getBookingTravelDate(booking),
                                                                                    booking?.booking_type?.code
                                                                                )
                                                                            }
                                                                        >
                                                                            {t('View details')}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious onClick={handlePrevious} disabled={page === 0} />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext onClick={handleNext} disabled={page >= totalPages - 1} />
                    </Pagination>
                    <Divider />
                </div>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={handleErrClose}
                    isMentee={false}
                />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title={t('Success')} message={successMessage} onClose={handleSucClose} />
            )}
        </>
    )
}
